/**
 * Session check telemetry.
 *
 * Records what the periodic "am I still logged in?" check saw whenever it
 * signs a user out, or declines to because the server couldn't be reached.
 * Those moments usually happen while the network is down, so records are
 * kept in localStorage and sent through jserror/log after the next
 * successful session check or login (when the username is known again).
 */

// NOTE: App and api are accessed lazily via (window as any) because
// app-compat.ts imports from this file (circular dependency).

import { getCurrentUsername } from '@/utils/auth';

export type SessionCheckState = 'yes' | 'no' | 'unknown';

export interface SessionCheckResult {
  state: SessionCheckState;
  /** HTTP status, 0 for a network failure, -1 for an unparseable 200 */
  status: number;
  durationMs: number;
}

interface SessionCheckRecord {
  event: 'signout' | 'skipped-signout' | 'recovered';
  /** Who was signed in when it happened (the flush may run under a later login) */
  username: string;
  trigger: string;
  first: SessionCheckResult;
  second?: SessionCheckResult;
  at: string;
  lastAt: string;
  count: number;
  online: boolean;
  connection: string;
  visibility: string;
  platform: string;
  sinceLastYesSec: number | null;
}

const STORAGE_KEY = 'SessionCheckTelemetry';
const MAX_RECORDS = 20;
// Consecutive skipped sign-outs within this window collapse into one record
// (an officer offline for an hour would otherwise produce 20 identical rows).
const STREAK_WINDOW_MS = 10 * 60 * 1000;

let _lastYesTime: number | null = null;

function loadRecords(): SessionCheckRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveRecords(records: SessionCheckRecord[]): void {
  try {
    if (records.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(-MAX_RECORDS)));
    }
  } catch (e) {
    // Storage full or unavailable — telemetry is best-effort
  }
}

function getPlatform(): string {
  const w = window as any;
  if (w.isIOSApp) return 'ios-app';
  if (w.isAndroidApp) return 'android-app';
  if (w.isWindowsAppSdkApp) return 'windows-app';
  return 'browser';
}

function getConnection(): string {
  const c = (navigator as any).connection;
  if (!c) return 'n/a';
  return [c.type, c.effectiveType].filter(Boolean).join('/') || 'n/a';
}

export function recordSessionCheck(
  event: SessionCheckRecord['event'],
  trigger: string,
  first: SessionCheckResult,
  second?: SessionCheckResult
): void {
  const now = new Date();
  const records = loadRecords();
  const last = records[records.length - 1];

  if (event === 'skipped-signout' && last && last.event === 'skipped-signout'
      && last.trigger === trigger && now.getTime() - new Date(last.lastAt).getTime() < STREAK_WINDOW_MS) {
    last.count++;
    last.lastAt = now.toISOString();
    saveRecords(records);
    return;
  }

  records.push({
    event,
    username: getCurrentUsername(),
    trigger,
    first,
    second,
    at: now.toISOString(),
    lastAt: now.toISOString(),
    count: 1,
    online: navigator.onLine,
    connection: getConnection(),
    visibility: document.visibilityState,
    platform: getPlatform(),
    sinceLastYesSec: _lastYesTime !== null ? Math.round((now.getTime() - _lastYesTime) / 1000) : null
  });
  saveRecords(records);
}

/** Note a confirmed-logged-in result and send any queued records. */
export function noteSessionCheckYes(): void {
  _lastYesTime = Date.now();
  flushSessionCheckTelemetry();
}

export function flushSessionCheckTelemetry(): void {
  const records = loadRecords();
  if (records.length === 0) return;

  const w = window as any;
  if (!w.api || !w.JSErrorType) return;

  saveRecords([]);
  for (const r of records) {
    w.api.insertNewJSError('SessionCheck ' + JSON.stringify(r), null, r.event, w.JSErrorType.CAUGHT, null);
  }
}
